import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import chatBackgroundImg from '@trinity/chat-components/static/images/chat-background.png';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useMessageGenerationSocket } from '@trinity/chat-components/websocket/hooks/useMessageGenerationSocket.js';
import { ChatInterfaceNative } from '../Segmentation/components/ChatInterfaceNative';
import { MessageGeneratorPanel } from './MessageGeneratorPanel';
import type { MarketingMessageRow } from '../MessageLibrary/MessageEditorModal';

interface MessageGeneratorScreenProps {
  initialSegmentationSessionId?: string;
  initialSegmentId?: string;
}

export function MessageGeneratorScreen({
  initialSegmentationSessionId,
  initialSegmentId,
}: MessageGeneratorScreenProps = {}): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apiClient = useChatApiClient();

  // Check URL params for sessionId and segmentId, or use props
  const urlSessionId = initialSegmentationSessionId ?? searchParams.get('segmentationSessionId');
  const urlSegmentId = initialSegmentId ?? searchParams.get('segmentId');

  // State for message gen session
  const [messageGenSessionId, setMessageGenSessionId] = useState<string | null>(urlSessionId);
  const [segmentIdentifier, setSegmentIdentifier] = useState<string | null>(urlSegmentId);
  const [runName, setRunName] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // State for continue button
  const [showContinueButton, setShowContinueButton] = useState(false);
  const [continueHandler, setContinueHandler] = useState<(() => void) | null>(null);
  const [selectionLabel, setSelectionLabel] = useState<string | null>(null);

  // Messages from agent state
  const [messages, setMessages] = useState<MarketingMessageRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Connect to message generation WebSocket
  const messageGenSocket = useMessageGenerationSocket(messageGenSessionId ?? undefined);

  // Listen for state updates via WebSocket
  useEffect(() => {
    if (!messageGenSessionId || !messageGenSocket) return;

    // Fetch initial messages
    const fetchMessages = async () => {
      try {
        const response = await apiClient.authenticatedClient.get<MarketingMessageRow[]>(
          `/message-gen-sessions/${messageGenSessionId}/messages`
        );
        setMessages(response.data);
      } catch (error) {
        console.error('[MessageGeneratorScreen] Failed to fetch messages:', error);
      }
    };

    fetchMessages();

    // Subscribe to WebSocket events for state updates
    const handleStateUpdate = (payload: { messages?: MarketingMessageRow[] }) => {
      if (payload?.messages) {
        console.log('[MessageGeneratorScreen] Received state-snapshot-updated, messages count:', payload.messages.length);
        setMessages(payload.messages);
      }
    };

    messageGenSocket.on('state-snapshot-updated', handleStateUpdate);

    return () => {
      messageGenSocket.off('state-snapshot-updated', handleStateUpdate);
    };
  }, [messageGenSessionId, messageGenSocket, apiClient]);

  const handleSaveMessages = useCallback(async () => {
    if (!messageGenSessionId || isSaving) return;
    setIsSaving(true);
    try {
      await apiClient.authenticatedClient.post(
        `/message-gen-sessions/${messageGenSessionId}/messages/save`,
      );
    } catch (error) {
      console.error('[MessageGeneratorScreen] Failed to save messages:', error);
    } finally {
      setIsSaving(false);
    }
  }, [messageGenSessionId, isSaving, apiClient]);

  // Auto-create session when URL params are provided
  useEffect(() => {
    if (!urlSessionId || messageGenSessionId || isCreating) return;

    setIsCreating(true);

    // If segmentId provided, create session for segment
    // Otherwise create for entire run (will fetch segments)
    const createSession = async () => {
      try {
        if (urlSegmentId) {
          // Start session with single segment
          const response = await apiClient.authenticatedClient.post<{ sessionId: string; chatInstanceId: string }>(
            '/message-gen-sessions/start',
            {
              initialState: {
                segmentIds: [urlSegmentId],
              },
            }
          );
          setMessageGenSessionId(response.data.sessionId);
          setSegmentIdentifier(urlSegmentId);
        } else {
          // Fetch segments for the run
          const { listSegments } = await import('../Segmentation/api/client');
          const segmentsResponse = await listSegments({ sessionId: urlSessionId });
          const segmentIds = segmentsResponse.segments.map((seg: any) => seg.identifier);

          // Start session with all segments
          const response = await apiClient.authenticatedClient.post<{ sessionId: string; chatInstanceId: string }>(
            '/message-gen-sessions/start',
            {
              initialState: {
                segmentIds,
              },
            }
          );
          setMessageGenSessionId(response.data.sessionId);
        }
      } catch (error) {
        console.error('[MessageGeneratorScreen] Failed to auto-create session:', error);
      } finally {
        setIsCreating(false);
      }
    };

    createSession();
  }, [urlSessionId, urlSegmentId, messageGenSessionId, isCreating, apiClient]);

  const handleSelectionChange = useCallback((hasSelection: boolean, handler: (() => void) | null, label: string | null) => {
    setShowContinueButton(hasSelection);
    setContinueHandler(() => handler);
    setSelectionLabel(label);
  }, []);

  const handleSelectRun = async (segmentationSessionId: string, segmentIds: string[], runNameParam: string): Promise<void> => {
    setIsCreating(true);
    try {
      // Start a new message gen session - backend generates the ID
      const response = await apiClient.authenticatedClient.post<{ sessionId: string; chatInstanceId: string }>(
        '/message-gen-sessions/start',
        {
          initialState: {
            segmentIds,
          },
        }
      );
      // Use the backend-generated session ID
      setMessageGenSessionId(response.data.sessionId);
      setSegmentIdentifier(null); // No specific segment
      setRunName(runNameParam);
    } catch (error) {
      console.error('[MessageGeneratorScreen] Failed to create message gen session:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSelectSegment = async (segmentationSessionId: string, segmentId: string, segmentName: string, runNameParam: string): Promise<void> => {
    setIsCreating(true);
    try {
      // Start a new message gen session - backend generates the ID
      const response = await apiClient.authenticatedClient.post<{ sessionId: string; chatInstanceId: string }>(
        '/message-gen-sessions/start',
        {
          initialState: {
            segmentIds: [segmentId],
          },
        }
      );
      // Use the backend-generated session ID
      setMessageGenSessionId(response.data.sessionId);
      setSegmentIdentifier(segmentId);
      setRunName(`${runNameParam} - ${segmentName}`);
    } catch (error) {
      console.error('[MessageGeneratorScreen] Failed to create message gen session:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const isEmbedded = initialSegmentationSessionId !== undefined;

  return (
    <Root>
      <MainContent>
        {!isEmbedded && (
          <BackButtonContainer>
            <BackButton
              onClick={() => navigate(-1)}
              title="Go back"
            >
              &larr; Back
            </BackButton>
          </BackButtonContainer>
        )}

        <ChatContainer>
          {!messageGenSessionId ? (
            <ContinueOverlay>
              <ContinueContent>
                <SelectedCard>
                  {showContinueButton
                    ? selectionLabel
                    : 'Select a segmentation run and optionally a segment to begin chatting with message generator agent'
                  }
                </SelectedCard>
                <ContinueButton
                  onClick={() => continueHandler?.()}
                  disabled={!showContinueButton || isCreating}
                >
                  {isCreating ? 'Starting...' : 'Start Message Generation Agent'}
                </ContinueButton>
              </ContinueContent>
            </ContinueOverlay>
          ) : (
            <ChatInterfaceNative
              initialSegmentationSessionId={messageGenSessionId}
              segmentIdentifier={segmentIdentifier}
              runName={runName}
              disabled={!messageGenSessionId || isCreating}
            />
          )}
        </ChatContainer>
      </MainContent>

      <PanelContainer>
        <MessageGeneratorPanel
          mode={messageGenSessionId ? 'messages' : 'selector'}
          messages={messages}
          onSaveMessages={handleSaveMessages}
          isSaving={isSaving}
          onSelectRun={handleSelectRun}
          onSelectSegment={handleSelectSegment}
          onSelectionChange={handleSelectionChange}
        />
      </PanelContainer>
    </Root>
  );
}

// Styled Components
const Root = styled.div`
  display: flex;
  height: calc(100vh - 60px);
  overflow: hidden;
  font-family: 'HarmoniaSans W01', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  background: url(${chatBackgroundImg}) no-repeat center;
  background-size: cover;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
`;

const BackButtonContainer = styled.div`
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.95);
  border-bottom: 1px solid #e0e0e0;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #626262;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  transition: color 0.2s ease;

  &:hover {
    color: ${props => props.theme.palette.sentiment.primary.main};
  }
`;

const ChatContainer = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  background: url(${chatBackgroundImg}) no-repeat center;
  background-size: cover;
`;

const PanelContainer = styled.div`
  width: 550px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ContinueOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const ContinueContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 500px;
  width: 100%;
  padding: 40px;
`;

const PlaceholderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  max-width: 600px;
  margin: 0 auto;
`;

const PlaceholderText = styled.div`
  font-size: 18px;
  color: #626262;
  text-align: center;
  line-height: 1.6;
`;

const SelectedCard = styled.div`
  width: 100%;
  padding: 20px 24px;
  background: white;
  border: 2px solid ${props => props.theme.palette.sentiment.primary.main};
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  color: #323232;
  text-align: center;
  box-shadow: 0 2px 8px ${props => props.theme.palette.sentiment.primary.main}1a;
`;

const ContinueButton = styled.button<{ disabled?: boolean }>`
  background: ${props => props.disabled ? '#cccccc' : props.theme.palette.sentiment.primary.main};
  color: white;
  border: none;
  border-radius: 10px;
  padding: 16px 40px;
  font-size: 16px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  box-shadow: 0 4px 12px ${props => props.disabled ? '#cccccc40' : props.theme.palette.sentiment.primary.main + '40'};
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background: ${props => props.theme.palette.sentiment.primary.dark};
    box-shadow: 0 6px 20px ${props => props.theme.palette.sentiment.primary.main}59;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 8px ${props => props.theme.palette.sentiment.primary.main}4d;
  }
`;
